/**
 * Role API Service
 *
 * API methods for role and permission management operations.
 * Updated to use standardized API response format.
 *
 * Global Roles Architecture:
 * - Roles are tenant-wide (not application-scoped)
 * - Permissions format: "<applicationCode>:<action>" (e.g., "inventory:read")
 * - Permissions validated against existing applications
 */

import apiClient from './client';
import type { ApiSuccessResponse, ApiCollectionResponse } from '../types/api.types';
import type {
  RoleSanitized,
  RoleCreateDTO,
  RoleUpdateDTO,
  RoleAssignDTO,
  RoleListQuery,
} from '../types/role.types';

// ============================================================================
// ROLE SERVICE
// ============================================================================

export const roleService = {
  /**
   * Create a new global role for a tenant
   * Permissions are validated against existing applications
   */
  createRole: async (
    tenantId: string,
    data: RoleCreateDTO
  ): Promise<RoleSanitized> => {
    const response = await apiClient.post<ApiSuccessResponse<RoleSanitized>>(
      `/tenants/${tenantId}/roles`,
      data
    );
    return response.data.data;
  },

  /**
   * List all global roles for a tenant
   */
  getRoles: async (
    tenantId: string,
    query?: RoleListQuery
  ): Promise<RoleSanitized[]> => {
    const response = await apiClient.get<ApiCollectionResponse<RoleSanitized>>(
      `/tenants/${tenantId}/roles`,
      {
        params: query,
      }
    );
    return response.data.data;
  },

  /**
   * List all roles that have permissions for a specific application
   */
  getRolesByApplication: async (
    tenantId: string,
    applicationId: string,
    query?: RoleListQuery
  ): Promise<RoleSanitized[]> => {
    const response = await apiClient.get<ApiCollectionResponse<RoleSanitized>>(
      `/tenants/${tenantId}/applications/${applicationId}/roles`,
      {
        params: query,
      }
    );
    return response.data.data;
  },

  /**
   * Get specific role details
   */
  getRole: async (tenantId: string, roleId: string): Promise<RoleSanitized> => {
    const response = await apiClient.get<ApiSuccessResponse<RoleSanitized>>(
      `/tenants/${tenantId}/roles/${roleId}`
    );
    return response.data.data;
  },

  /**
   * Update role permissions and metadata
   */
  updateRole: async (
    tenantId: string,
    roleId: string,
    data: RoleUpdateDTO
  ): Promise<RoleSanitized> => {
    const response = await apiClient.put<ApiSuccessResponse<RoleSanitized>>(
      `/tenants/${tenantId}/roles/${roleId}`,
      data
    );
    return response.data.data;
  },

  /**
   * Delete role and cascade to user/group assignments
   */
  deleteRole: async (tenantId: string, roleId: string): Promise<void> => {
    await apiClient.delete(`/tenants/${tenantId}/roles/${roleId}`);
  },

  /**
   * Assign role to users and/or groups
   */
  assignRole: async (
    tenantId: string,
    roleId: string,
    data: RoleAssignDTO
  ): Promise<RoleSanitized> => {
    const response = await apiClient.post<ApiSuccessResponse<RoleSanitized>>(
      `/tenants/${tenantId}/roles/${roleId}/assign`,
      data
    );
    return response.data.data;
  },

  /**
   * Unassign role from users and/or groups
   */
  unassignRole: async (
    tenantId: string,
    roleId: string,
    data: RoleAssignDTO
  ): Promise<RoleSanitized> => {
    const response = await apiClient.post<ApiSuccessResponse<RoleSanitized>>(
      `/tenants/${tenantId}/roles/${roleId}/unassign`,
      data
    );
    return response.data.data;
  },
};
